// Centralized Business Categories
// Used across the entire app for consistency
// Business owners select from these during registration
// Customers see these when booking appointments

export interface BusinessSubcategory {
  id: string;
  name: string;
  icon: string;
  description: string;
}

export interface BusinessCategory {
  id: string;
  name: string;
  icon: string;
  description: string;
  keywords: string[]; // For search functionality
  subcategories?: BusinessSubcategory[]; // Optional subcategories
}

export const BUSINESS_CATEGORIES: BusinessCategory[] = [
  {
    id: 'automotive',
    name: 'Automotive Services',
    icon: '🚗',
    description: 'Oil changes, repairs, maintenance, tire rotation, brake service',
    keywords: ['auto', 'car', 'vehicle', 'mechanic', 'car mechanic', 'auto shop', 'body shop', 'oil change', 'tires', 'brakes', 'repair', 'maintenance', 'car wash', 'detailing']
  },
  {
    id: 'beauty_wellness',
    name: 'Beauty & Wellness',
    icon: '💇‍♀️',
    description: 'Hair salon, spa, massage, facial treatments, makeup',
    keywords: ['hair', 'salon', 'haircut', 'styling', 'spa', 'massage', 'facial', 'beauty', 'makeup', 'skincare', 'nails', 'manicure', 'pedicure', 'waxing']
  },
  {
    id: 'health_fitness',
    name: 'Health & Fitness',
    icon: '💪',
    description: 'Gyms, personal training, yoga, pilates, fitness classes',
    keywords: ['gym', 'fitness', 'workout', 'training', 'yoga', 'pilates', 'crossfit', 'spinning', 'exercise', 'health']
  },
  {
    id: 'medical_dental',
    name: 'Medical & Dental',
    icon: '🏥',
    description: 'Doctor visits, dental care, therapy, checkups, vaccinations',
    keywords: ['doctor', 'medical', 'dental', 'dentist', 'therapy', 'checkup', 'vaccination', 'clinic', 'health', 'physical therapy']
  },
  {
    id: 'home_garden',
    name: 'Home & Garden',
    icon: '🏠',
    description: 'Cleaning, plumbing, electrical, landscaping, lawn care',
    keywords: ['home', 'house', 'cleaning', 'carpet cleaning', 'window washing', 'plumbing', 'electrical', 'handyman', 'repairs', 'HVAC', 'landscaping', 'lawn', 'mowing', 'gardening']
  },
  {
    id: 'pet_care',
    name: 'Pet Care',
    icon: '🐕',
    description: 'Grooming, vet visits, training, boarding, pet sitting',
    keywords: ['pet', 'dog', 'cat', 'grooming', 'vet', 'veterinarian', 'training', 'boarding', 'pet sitting', 'dog walking']
  },
  {
    id: 'technology',
    name: 'Technology Services',
    icon: '💻',
    description: 'Computer repair, IT support, phone repair, networking',
    keywords: ['tech', 'computer', 'IT', 'phone', 'repair', 'data recovery', 'setup', 'networking', 'software', 'hardware']
  },
  {
    id: 'food_dining',
    name: 'Food & Dining',
    icon: '🍽️',
    description: 'Restaurant reservations, catering, private chef services',
    keywords: ['restaurant', 'dining', 'food', 'catering', 'chef', 'meal prep', 'delivery']
  },
  {
    id: 'education',
    name: 'Education & Tutoring',
    icon: '📚',
    description: 'Tutoring, test prep, music lessons, language classes',
    keywords: ['tutor', 'tutoring', 'education', 'test prep', 'SAT', 'ACT', 'music', 'lessons', 'language', 'math', 'science']
  },
  {
    id: 'legal',
    name: 'Legal Services',
    icon: '⚖️',
    description: 'Legal consultations, notary, contracts, legal advice',
    keywords: ['lawyer', 'legal', 'attorney', 'notary', 'contract', 'legal advice', 'immigration', 'estate planning']
  },
  {
    id: 'financial',
    name: 'Financial Services',
    icon: '💰',
    description: 'Tax preparation, accounting, financial planning, bookkeeping',
    keywords: ['tax', 'accounting', 'bookkeeping', 'financial', 'CPA', 'financial planning', 'consulting']
  },
  {
    id: 'real_estate',
    name: 'Real Estate',
    icon: '🏢',
    description: 'Property viewing, consultations, buying, selling, rentals',
    keywords: ['real estate', 'property', 'home', 'house', 'buying', 'selling', 'rental', 'appraisal', 'realtor']
  },
  {
    id: 'event_planning',
    name: 'Event Planning',
    icon: '🎉',
    description: 'Weddings, parties, corporate events, event coordination',
    keywords: ['event', 'wedding', 'party', 'birthday', 'baby shower', 'corporate', 'conference', 'planning', 'coordination']
  },
  {
    id: 'photography',
    name: 'Photography & Video',
    icon: '📸',
    description: 'Event photography, portraits, commercial photography',
    keywords: ['photo', 'photography', 'photographer', 'video', 'videographer', 'wedding', 'portrait', 'headshot', 'commercial', 'product']
  },
  {
    id: 'construction',
    name: 'Construction & Renovation',
    icon: '🔨',
    description: 'Remodeling, renovation, painting, flooring, roofing',
    keywords: ['construction', 'remodel', 'renovation', 'painting', 'drywall', 'flooring', 'roofing', 'deck', 'building']
  },
  {
    id: 'moving_storage',
    name: 'Moving & Storage',
    icon: '📦',
    description: 'Moving services, packing, storage, junk removal',
    keywords: ['moving', 'movers', 'packing', 'unpacking', 'storage', 'junk removal', 'hauling']
  },
  {
    id: 'transportation',
    name: 'Transportation & Delivery',
    icon: '🚗',
    description: 'Taxi, ride-sharing, delivery, courier, airport shuttle, limousine',
    keywords: ['taxi', 'uber', 'lyft', 'ride', 'delivery', 'courier', 'shuttle', 'limousine', 'driver', 'transportation', 'airport']
  },
  {
    id: 'childcare',
    name: 'Childcare & Kids Services',
    icon: '👶',
    description: 'Daycare, babysitting, nanny, kids activities, party entertainment',
    keywords: ['childcare', 'daycare', 'babysitting', 'nanny', 'kids', 'children', 'party', 'entertainment', 'activity center', 'preschool']
  },
  {
    id: 'security_safety',
    name: 'Security & Safety Services',
    icon: '🛡️',
    description: 'Security systems, locksmith, home inspection, pest control',
    keywords: ['security', 'locksmith', 'lock', 'inspection', 'pest control', 'exterminator', 'alarm', 'safety', 'fire safety', 'emergency']
  },
  {
    id: 'digital_marketing',
    name: 'Digital Marketing & Media',
    icon: '🌐',
    description: 'Social media management, SEO, digital ads, content creation',
    keywords: ['marketing', 'digital', 'social media', 'SEO', 'ads', 'content', 'influencer', 'branding', 'web design', 'graphics']
  },
  {
    id: 'senior_care',
    name: 'Senior Care & Health Services',
    icon: '🧓',
    description: 'Home health aides, senior transportation, companion care, assisted living',
    keywords: ['senior', 'elderly', 'home health', 'caregiver', 'companion', 'assisted living', 'nursing', 'elder care', 'hospice']
  },
  {
    id: 'sports_coaching',
    name: 'Sports Coaching & Lessons',
    icon: '🏋️',
    description: 'Tennis, golf, swim lessons, sports coaching, athletic training',
    keywords: ['sports', 'coaching', 'coach', 'tennis', 'golf', 'swim', 'swimming', 'lessons', 'athletic', 'trainer', 'basketball', 'soccer']
  },
  {
    id: 'personal_styling',
    name: 'Personal Shopping & Styling',
    icon: '👗',
    description: 'Personal shopping, wardrobe styling, image consulting, fashion advice',
    keywords: ['shopping', 'personal shopper', 'styling', 'stylist', 'wardrobe', 'fashion', 'image', 'consulting', 'closet', 'outfit']
  },
  {
    id: 'halal',
    name: 'Halal Services',
    icon: '🕌',
    description: 'Halal food, halal restaurants, halal catering, halal meat, Islamic services',
    keywords: ['halal', 'halal food', 'halal restaurant', 'halal catering', 'halal meat', 'islamic', 'muslim', 'halal butcher', 'halal bakery', 'halal grocery', 'halal delivery', 'halal wedding', 'halal event'],
    subcategories: [
      { id: 'halal_meat', name: 'Halal Meat & Butcher', icon: '🥩', description: 'Halal butcher shops, meat markets, fresh halal meat' },
      { id: 'halal_restaurant', name: 'Halal Restaurants', icon: '🍽️', description: 'Halal dining, halal fast food, halal cuisine' },
      { id: 'halal_grocery', name: 'Halal Grocery', icon: '🛒', description: 'Halal supermarkets, halal food stores, halal products' },
      { id: 'halal_bakery', name: 'Halal Bakery', icon: '🥖', description: 'Halal baked goods, halal pastries, halal bread' },
      { id: 'halal_catering', name: 'Halal Catering', icon: '🍱', description: 'Halal catering services, halal event food, halal wedding catering' },
      { id: 'halal_delivery', name: 'Halal Delivery', icon: '🚚', description: 'Halal food delivery, halal meal kits, halal subscriptions' },
    ]
  },
];

// Helper function to get category by ID
export const getCategoryById = (id: string): BusinessCategory | undefined => {
  return BUSINESS_CATEGORIES.find(cat => cat.id === id);
};

// Helper function to get category name by ID
export const getCategoryName = (id: string): string => {
  const category = getCategoryById(id);
  return category ? category.name : 'Unknown Category';
};

// Helper function to search categories
export const searchCategories = (query: string): BusinessCategory[] => {
  if (!query.trim()) return BUSINESS_CATEGORIES;
  
  const lowerQuery = query.toLowerCase();
  return BUSINESS_CATEGORIES.filter(category => 
    category.name.toLowerCase().includes(lowerQuery) ||
    category.description.toLowerCase().includes(lowerQuery) ||
    category.keywords.some(keyword => keyword.toLowerCase().includes(lowerQuery))
  );
};

/** Map legacy free-text industry or stored category name to canonical category id. */
export function resolveCategoryIdFromStoredValue(stored: string | undefined | null): string | null {
  if (!stored?.trim()) return null;
  const trimmed = stored.trim();
  const byId = getCategoryById(trimmed);
  if (byId) return byId.id;

  const lower = trimmed.toLowerCase();
  const byName = BUSINESS_CATEGORIES.find((c) => c.name.toLowerCase() === lower);
  if (byName) return byName.id;

  const byKeyword = BUSINESS_CATEGORIES.find((c) =>
    c.keywords.some((keyword) => {
      const kl = keyword.toLowerCase();
      return kl === lower || lower.includes(kl) || kl.includes(lower);
    }),
  );
  if (byKeyword) return byKeyword.id;

  const normalizedId = lower.replace(/\s+/g, '_').replace(/&/g, 'and');
  const byNormalizedId = BUSINESS_CATEGORIES.find((c) => c.id === normalizedId);
  if (byNormalizedId) return byNormalizedId.id;

  return null;
}

/** Normalize owner/business stored values to canonical id + display label. */
export function normalizeBusinessCategoryFields(
  storedCategoryId?: string | null,
  storedIndustry?: string | null,
): { categoryId: string; industry: string } {
  if (storedCategoryId && getCategoryById(storedCategoryId)) {
    return { categoryId: storedCategoryId, industry: getCategoryName(storedCategoryId) };
  }
  const resolved =
    resolveCategoryIdFromStoredValue(storedIndustry) ||
    resolveCategoryIdFromStoredValue(storedCategoryId);
  if (resolved) {
    return { categoryId: resolved, industry: getCategoryName(resolved) };
  }
  return {
    categoryId: '',
    industry: storedIndustry?.trim() || storedCategoryId?.trim() || '',
  };
}

// Export total count
export const TOTAL_CATEGORIES = BUSINESS_CATEGORIES.length;

